import React from 'react';
import { Link } from 'react-router-dom';
import LegalLayout, { Clause, Fact, Email } from './LegalLayout';
import { LEGAL } from './legalDetails';

export default function TermsOfService() {
  return (
    <LegalLayout title="Terms of Service">

      <div className="lg-callout">
        <p>
          These terms apply to organisations that use Pulso to receive and manage messages. If you are a
          community member or worker sending a message, our <Link to="/privacy">Privacy Policy</Link> explains
          how your information is handled.
        </p>
      </div>

      <Clause n={1}>About these terms</Clause>
      <p>
        Pulso is provided by <strong>{LEGAL.companyName}</strong> (registration number <Fact value={LEGAL.regNumber} />)
        ("we", "us"). These terms are an agreement between us and the organisation that signs up for or uses Pulso
        ("you", the "client"). By creating an account or using Pulso, you agree to them on behalf of your organisation.
      </p>
      <p>
        If you have a separate signed agreement with us, that agreement takes priority wherever it differs from these terms.
      </p>

      <Clause n={2}>The service</Clause>
      <p>
        Pulso lets you receive reports, grievances and feedback from workers and community members by WhatsApp,
        SMS, email and web form. It translates them, classifies them by theme and sentiment, routes them to the
        right site, and shows them in a management dashboard where your team can review and reply.
      </p>
      <p>
        Classification and translation are partly automated using AI, and they can be wrong. Treat them as an aid
        for sorting messages, not a substitute for your own team reading and judging reports, especially urgent
        or safety-related ones.
      </p>

      <Clause n={3}>Your account</Clause>
      <ul>
        <li>Keep your login details confidential. Only give access to people in your organisation who need it.</li>
        <li>You're responsible for what happens under your organisation's accounts.</li>
        <li>Tell us promptly at <Email value={LEGAL.contactEmail} /> if you think an account has been compromised.</li>
      </ul>

      <Clause n={4}>Your responsibilities for personal information</Clause>
      <p>
        For the messages you receive through Pulso, your organisation is the "responsible party" under the
        Protection of Personal Information Act, 2013 (POPIA), and we are your "operator". That means you are
        responsible for:
      </p>
      <ul>
        <li>having a lawful reason to collect and use the personal information people send you;</li>
        <li>telling the people who contact you how their information will be used, for example in your own privacy notice;</li>
        <li>dealing with requests from people who want to access, correct or delete their information (we'll help, see our <Link to="/data-deletion">data deletion instructions</Link>);</li>
        <li>only using what people send you for the purposes you collected it for.</li>
      </ul>
      <p>
        We will process that information only on your instructions and as described in our{' '}
        <Link to="/privacy">Privacy Policy</Link>, keep it secure, and tell you without undue delay if we become
        aware of a security breach affecting it.
      </p>

      <Clause n={5}>WhatsApp and Meta policies</Clause>
      <p>If you use Pulso with WhatsApp:</p>
      <ul>
        <li>
          You're responsible for your own <strong>WhatsApp Business Account</strong>, including the business
          profile, phone number and display name, and for keeping it in good standing.
        </li>
        <li>
          You must comply with Meta's policies, including the WhatsApp Business Terms of Service, the WhatsApp
          Business Messaging Policy and the WhatsApp Commerce Policy, as they change from time to time.
        </li>
        <li>
          You must only send messages people have agreed to receive, honour requests to stop, and follow
          WhatsApp's rules on message templates and messaging windows.
        </li>
        <li>
          Meta, not us, decides whether to approve, restrict or suspend a WhatsApp Business Account or phone
          number. We can't guarantee WhatsApp access, and we aren't responsible if Meta limits or removes it.
        </li>
      </ul>
      <p>
        The same applies to the rules of our other messaging providers, including Twilio's acceptable use policy,
        and to any laws on sending SMS or email.
      </p>

      <Clause n={6}>Acceptable use</Clause>
      <p>You must not use Pulso to:</p>
      <ul>
        <li>break any law, including POPIA and laws on electronic communications and marketing;</li>
        <li>send spam, unsolicited bulk messages, or misleading, threatening, abusive or discriminatory content;</li>
        <li>harass, intimidate or retaliate against anyone for raising a report or grievance;</li>
        <li>try to identify people who chose to report anonymously, where they're entitled to that anonymity;</li>
        <li>access another organisation's data, or try to get around Pulso's security;</li>
        <li>overload, disrupt, copy or reverse engineer the service.</li>
      </ul>
      <p>
        We may suspend access, after warning you where reasonably possible, if we believe these terms or the law
        are being broken, or if it's needed to protect people, our service or our providers.
      </p>

      <Clause n={7}>Your data</Clause>
      <p>
        The messages and information your organisation receives through Pulso belong to you (and to the people who
        sent them). You give us permission to use them only to provide, secure, and support the service for you.
        When your agreement with us ends, we'll send you an export of your organisation's data so you can still
        review past reports, and then delete it from Pulso. From then on you're responsible for keeping that export
        secure and handling it in line with POPIA.
      </p>

      <Clause n={8}>Fees</Clause>
      <p>
        Fees, if any, are set out in your plan or your agreement with us. Our messaging providers' charges, such as
        WhatsApp conversation fees, may be passed on as described there.
      </p>

      <Clause n={9}>Availability</Clause>
      <p>
        We work to keep Pulso available and reliable, but we don't promise it will be uninterrupted or error-free,
        unless your agreement with us sets a specific service level. Pulso depends on third parties such as Twilio,
        Meta, email providers, hosting providers and AI providers, and outages on their side can affect it. We may
        need to carry out maintenance, and we'll try to give notice of planned downtime.
      </p>
      <p>
        <strong>Pulso is not an emergency service.</strong> Make sure the people who contact you know how to reach
        emergency services directly.
      </p>

      <Clause n={10}>Limits on our liability</Clause>
      <p>To the extent the law allows:</p>
      <ul>
        <li>Pulso is provided "as is", and we don't give any warranties beyond those in these terms or your agreement.</li>
        <li>We aren't liable for indirect or consequential loss, such as lost profits, lost business or reputational harm.</li>
        <li>We aren't liable for the actions of Meta, Twilio or other third-party services, or for decisions you make based on Pulso's automated classification.</li>
        <li>Our total liability to you for any claim is limited to <Fact value={LEGAL.liabilityCap} />.</li>
      </ul>
      <p>Nothing in these terms limits liability that cannot legally be limited.</p>

      <Clause n={11}>Ending the service</Clause>
      <p>
        You can stop using Pulso at any time, subject to any term in your agreement. We may end the service on
        notice as set out in your agreement, or immediately if you seriously breach these terms. When the service
        ends, we'll send you an export of your organisation's data and then delete it from Pulso, as described in
        <a href="#section-7">section 7</a> and our <Link to="/privacy">Privacy Policy</Link>.
      </p>

      <Clause n={12}>Changes to these terms</Clause>
      <p>
        We may update these terms. We'll give you at least <Fact value={LEGAL.termsNoticePeriod} /> notice of
        significant changes. If you keep using Pulso after a change takes effect, the new terms apply.
      </p>

      <Clause n={13}>Governing law</Clause>
      <p>
        These terms are governed by the laws of the Republic of South Africa, and the courts of South Africa have
        jurisdiction over any dispute about them.
      </p>

      <Clause n={14}>Contact</Clause>
      <dl className="lg-contact">
        <div><dt>Company</dt><dd>{LEGAL.companyName}</dd></div>
        <div><dt>Address</dt><dd><Fact value={LEGAL.address} /></dd></div>
        <div><dt>Email</dt><dd><Email value={LEGAL.contactEmail} /></dd></div>
      </dl>

    </LegalLayout>
  );
}
